import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, 
  TrendingDown,
  Award,
  Target,
  Activity,
  Zap,
  Calendar,
  ChevronDown
} from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const ResultsInsight = ({ userId, discipline }) => {
  const [dataExists, setDataExists] = useState(true);
  const [timeframe, setTimeframe] = useState('12');
  const [routineType, setRoutineType] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('total_score');
  const [jsonData, setJsonData] = useState(null);
  const [yDomain, setYDomain] = useState(null);
  const [isError, setIsError] = useState(false);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
        axios.get(process.env.REACT_APP_API_HOST + `/api/results/${discipline}/${userId}`)
        .then(response => {
            const data = response.data;
            if (data !== null) {
                setJsonData(data.map(entry => ({
                    ...entry,
                    result_date: new Date(entry.result_date).toLocaleDateString('en-GB')
                })).sort((a, b) => new Date(b.result_date) - new Date(a.result_date)));
            }
            else {
                setDataExists(false);
            }
        })
        .catch(error => {
            console.error('Error fetching results: ', error);
            setIsError(true);
        });
    }, [userId, discipline]);

  useEffect(() => {
    if (jsonData) {
      const filteredData = jsonData.filter(entry => 
        routineType === 'all' || entry.routine_type === routineType
      );
      
      if (selectedMetric === 'total_score') {
        const scores = filteredData.map(e => e.total_score);
        const minY = Math.floor(Math.min(...scores) - 2);
        const maxY = Math.ceil(Math.max(...scores) + 2);
        setYDomain([minY, maxY]);
      } else {
        const values = filteredData.map(e => e[selectedMetric]);
        const minY = Math.floor(Math.min(...values) - 1);
        const maxY = Math.ceil(Math.max(...values) + 1);
        setYDomain([minY, maxY]);
      }
    }
  }, [jsonData, selectedMetric, routineType]);

  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-6 shadow-lg text-center max-w-sm">
          <p className="text-red-600 font-semibold">Error loading data</p>
        </div>
      </div>
    );
  }

  if (!dataExists) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-6 shadow-lg text-center max-w-sm">
          <Calendar className="text-gray-400 mx-auto mb-4" size={48} />
          <h3 className="text-lg font-bold text-gray-900 mb-2">No Results Yet</h3>
          <p className="text-sm text-gray-600">Start logging competition results to see your progress here!</p>
        </div>
      </div>
    );
  }

  if (jsonData === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading results...</p>
        </div>
      </div>
    );
  }

  const calculateCutoffDate = (timeframe) => {
    const now = new Date();
    switch (timeframe) {
      case '3':
        return new Date(now.setMonth(now.getMonth() - 3));
      case '6':
        return new Date(now.setMonth(now.getMonth() - 6));
      case '12':
        return new Date(now.setFullYear(now.getFullYear() - 1));
      case 'all':
      default:
        return null;
    }
  };

  const cutoffDate = calculateCutoffDate(timeframe);
  const filteredData = jsonData
    .filter(entry => routineType === 'all' || entry.routine_type === routineType)
    .filter(entry => {
      const entryDate = new Date(entry.result_date.split('/').reverse().join('-'));
      return !cutoffDate || entryDate >= cutoffDate;
    })
    .filter(entry => !(entry.score_input_type === 'total' && selectedMetric !== 'total_score'));

  const metrics = discipline === 'trampoline' 
    ? [
        { id: 'total_score', label: 'Total Score', icon: Award, color: '#3b82f6' },
        { id: 'execution', label: 'Execution', icon: Target, color: '#8b5cf6' },
        { id: 'hd', label: 'HD', icon: Activity, color: '#10b981' },
        { id: 'tof', label: 'Time of Flight', icon: Zap, color: '#f59e0b' },
        { id: 'difficulty', label: 'Difficulty', icon: TrendingUp, color: '#ef4444' },
      ]
    : [
        { id: 'total_score', label: 'Total Score', icon: Award, color: '#3b82f6' },
        { id: 'execution', label: 'Execution', icon: Target, color: '#8b5cf6' },
        { id: 'difficulty', label: 'Difficulty', icon: TrendingUp, color: '#ef4444' },
      ];

  const selectedMetricData = metrics.find(m => m.id === selectedMetric);

  // Calculate summary stats
  const totalComps = filteredData.length;
  const avgScore = totalComps > 0 
    ? (filteredData.reduce((sum, e) => sum + e.total_score, 0) / totalComps).toFixed(2)
    : 0;
  const highestScore = totalComps > 0 
    ? Math.max(...filteredData.map(e => e.total_score)).toFixed(2)
    : 0;
  const latestScore = filteredData.length > 0 ? filteredData[0].total_score.toFixed(2) : 0;

  const scoreChange = filteredData.length >= 2
    ? ((filteredData[0].total_score - filteredData[1].total_score) / filteredData[1].total_score * 100).toFixed(0)
    : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
          <p className="text-sm font-semibold text-gray-900 mb-2">{data.event}</p>
          <p className="text-xs text-gray-600 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="text-xs text-gray-600">{selectedMetricData.label}:</span>
              <span className="text-sm font-bold" style={{ color: entry.color }}>
                {entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-3 pb-6 new-entry">
      <div className="max-w-2xl mx-auto space-y-4">
        
        {/* Header */}
        <div>
          <h2 className="text-sm text-gray-600 mt-0.5 capitalize">
            {discipline} Results • {routineType === 'all' ? 'All Routines' : routineType === 'vol' ? 'Voluntary' : 'Compulsory'}
          </h2>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          {discipline === 'trampoline' && (
            <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-200 flex gap-1">
              <button
                onClick={() => setRoutineType('vol')}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                  routineType === 'vol'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-base">Voluntary</span>
              </button>
              <button
                onClick={() => setRoutineType('set')}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                  routineType === 'set'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-base">Compulsory</span>
              </button>
              <button
                onClick={() => setRoutineType('all')}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                  routineType === 'all'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-base">All</span>
              </button>
            </div>
          )}

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <label className="block text-base font-semibold text-gray-700 mb-2">Timeframe</label>
            <div className="relative">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="w-full appearance-none bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 pr-10 text-gray-800 font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                style={{ fontSize: '1rem' }}
              >
                <option value="3" className='text-base'>Last 3 Months</option>
                <option value="6" className='text-base'>Last 6 Months</option>
                <option value="12" className='text-base'>Last 12 Months</option>
                <option value="all" className='text-base'>All Time</option>
              </select>
              <ChevronDown size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-blue-100 rounded-lg p-1.5">
                <Calendar className="text-blue-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Competitions</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalComps}</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-purple-100 rounded-lg p-1.5">
                <Award className="text-purple-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Avg Score</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{avgScore}</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-green-100 rounded-lg p-1.5">
                <TrendingUp className="text-green-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Highest Score</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{highestScore}</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-orange-100 rounded-lg p-1.5">
                <Target className="text-orange-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Latest Score</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-gray-900">{latestScore}</p>
              {scoreChange !== 0 && filteredData.length >= 2 && (
                <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                  scoreChange >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {scoreChange >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {Math.abs(scoreChange)}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Metric Selector */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <label className="block text-base font-semibold text-gray-700 mb-2">
            Select Metric to Visualize
          </label>
          <div className="relative">
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="w-full appearance-none bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 pr-10 text-gray-800 font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
              style={{ fontSize: '1rem' }}
            >
              {metrics.map(metric => (
                <option key={metric.id} value={metric.id} className='text-base'>
                  {metric.label}
                </option>
              ))}
            </select>
            <ChevronDown size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>
        </div>

        {/* Main Chart */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="rounded-lg p-1.5" style={{ backgroundColor: `${selectedMetricData.color}20` }}>
              <selectedMetricData.icon className="text-gray-700" size={18} style={{ color: selectedMetricData.color }} />
            </div>
            <h3 className="text-base font-bold text-gray-900">{selectedMetricData.label} Progression</h3>
          </div>
          
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={[...filteredData].reverse()}>
              <defs>
                <linearGradient id={`color${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={selectedMetricData.color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={selectedMetricData.color} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="result_date" 
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <YAxis 
                domain={yDomain}
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey={selectedMetric} 
                stroke={selectedMetricData.color}
                strokeWidth={3}
                fill={`url(#color${selectedMetric})`}
                dot={{ fill: selectedMetricData.color, r: 4 }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Data Table Toggle */}
        <button
          onClick={() => setShowTable(!showTable)}
          className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-semibold text-gray-900">
            {showTable ? 'Hide' : 'Show'} Detailed Results Table
          </span>
          <ChevronDown 
            size={20} 
            className={`text-gray-600 transition-transform ${showTable ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Detailed Table */}
        {showTable && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-gray-600">Event</th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Score</th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Exec</th>
                    {discipline === 'trampoline' && (
                      <>
                        <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">HD</th>
                        <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">ToF</th>
                      </>
                    )}
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Diff</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.map((result, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-xs font-medium text-gray-900">{result.result_date}</td>
                      <td className="px-3 py-3 text-xs text-gray-600">{result.event}</td>
                      <td className="px-3 py-3 text-xs text-gray-600 text-right font-semibold">{result.total_score}</td>
                      <td className="px-3 py-3 text-xs text-gray-600 text-right">{result.execution}</td>
                      {discipline === 'trampoline' && (
                        <>
                          <td className="px-3 py-3 text-xs text-gray-600 text-right">{result.hd}</td>
                          <td className="px-3 py-3 text-xs text-gray-600 text-right">{result.tof}</td>
                        </>
                      )}
                      <td className="px-3 py-3 text-xs text-gray-600 text-right">{result.difficulty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ResultsInsight;